import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Modal,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { FormProvider, useForm, UseFormReturn } from "react-hook-form";
import {
  RHFAutocomplete,
  RHFDateTimePicker,
  RHFRadioGroup,
  RHFTextField,
  RHFTimePicker,
  RHFCheckbox,
} from "../../../../../../../RHFComponents/RHFComponents";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { pgmedg, pgmedtage } from "../../../../../../../../networking";
import { formatHour } from "../../../../../../../AppointmentResearch/AppointmentResearch";
import { AllertMessage } from "../MedintAgendaFormDetail";

type Fascia = { dalle: any; alle: any };

type GiornateForm = {
  giornateForm: {
    giornoSett: string;
    giornoSettDescr: string;
    numMaxPrest: string;
    durataStand: string;
    fasce: Fascia[];
    modGest: "0" | "1" | "2";
    dataDec: any;
    agendaGiorniAtt: string;
    primo: boolean;
    secondo: boolean;
    terzo: boolean;
    quarto: boolean;
    quinto: boolean;
  };
};

type Props = {
  open: boolean;
  onClose: () => void;
  action: "insert" | "edit" | "copy";
  initialData?: any;
  parentMethods: UseFormReturn<any>;
  selectedTipologiaId: string | null;
  createdIdPgmedt?: string;
};

const buildDefault = (): GiornateForm => ({
  giornateForm: {
    giornoSett: "",
    giornoSettDescr: "",
    numMaxPrest: "0",
    durataStand: "0",
    fasce: Array.from({ length: 5 }).map(() => ({ dalle: null, alle: null })),
    modGest: "0",
    dataDec: null,
    agendaGiorniAtt: "0",
    primo: false,
    secondo: false,
    terzo: false,
    quarto: false,
    quinto: false,
  },
});

const to01 = (b: boolean) => (b ? "1" : "0");

//Siccome l'api si aspetta una stringa tipo 1050 e non 10:50,
//questa funzione di helper serve per rimuovere i : dalla stringa
const toHHmmOr0 = (v: any) => {
  if (!v) return "0";
  if (dayjs.isDayjs(v)) return v.format("HHmm");
  if (typeof v === "string" && /^\d{2}:\d{2}$/.test(v))
    return v.replace(":", "");
  // se arriva "1050" dall'api è già okay
  if (typeof v === "string" && /^\d{3,4}$/.test(v)) return v.padStart(4, "0");
  return "0";
};

// creata solo per visualizzazione struttura
const PROVVISORIA = (props: {
  methods: UseFormReturn<any>;
  index: number;
  disabled?: boolean;
}) => {
  return (
    <Grid
      container
      spacing={2}
      display={"flex"}
      alignContent={"stretch"}
      alignItems={"stretch"}
    >
      <Grid item xs={6}>
        <RHFTimePicker
          control={props.methods.control}
          name={`giornateForm.fasce.${props.index}.dalle`}
          label="Dalle"
          minutesStep={5}
          size="small"
          disabled={props.disabled}
        />
      </Grid>
      <Grid item xs={6}>
        <RHFTimePicker
          control={props.methods.control}
          name={`giornateForm.fasce.${props.index}.alle`}
          label="Alle"
          minutesStep={5}
          size="small"
          disabled={props.disabled}
        />
      </Grid>
    </Grid>
  );
};

const GiornateModal = (props: Props) => {
  const giornate = [
    { label: "Lunedì", value: "1" },
    { label: "Martedì", value: "2" },
    { label: "Mercoledi", value: "3" },
    { label: "Giovedi", value: "4" },
    { label: "Venerdi", value: "5" },
    { label: "Sabato", value: "6" },
    { label: "Domenica", value: "7" },
  ];

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showAllert, setShowAllert] = useState(false);
  const [severity, setSeverity] = useState<"warning" | "success" | "error">(
    "warning",
  );
  const [message, setMessage] = useState("");

  const [pendingSubmit, setPendingSubmit] = useState<GiornateForm | null>(
    null,
  );

  const modalMethods = useForm<GiornateForm>({
    defaultValues: buildDefault(),
    mode: "onChange",
  });

  const fasce = modalMethods.watch("giornateForm.fasce");
  const modGestRaw = modalMethods.watch("giornateForm.modGest");

  // normalizzo: se arriva 0/1/2 number o string, qui diventa sempre "0"/"1"/"2"
  const modGest = String(modGestRaw ?? "0") as "0" | "1" | "2";

  const isNo = modGest === "0";
  const isSettimanale = modGest === "1";
  const isMensile = modGest === "2";

  //Funzioni che tengono traccia delle fasce per capire
  //se sono compilate e abilitano quelle successive nel caso lo siano
  const isFasciaCompilata = (i: number) => {
    const dalle = toHHmmOr0(fasce?.[i]?.dalle);
    const alle = toHHmmOr0(fasce?.[i]?.alle);

    const isEmpty = (v: string) => v === "0" || v === "0000";
    return !isEmpty(dalle) && !isEmpty(alle);
  };

  const isFasciaEnabled = (i: number) => {
    if (i === 0) return true;
    return isFasciaCompilata(i - 1);
  };

  useEffect(() => {
    const code = modalMethods.getValues("giornateForm.giornoSett");
    const label =
      giornate.find((g) => String(g.value) === String(code))?.label ?? "";
    modalMethods.setValue("giornateForm.giornoSettDescr", label, {
      shouldDirty: false,
      shouldValidate: false,
      shouldTouch: false,
    });
  }, [modalMethods.watch("giornateForm.giornoSett")]);

  useEffect(() => {
    if (!props.open) return;
    setError(null);

    if (props.action === "insert") {
      modalMethods.reset(buildDefault());
      return;
    }

    modalMethods.reset({
      giornateForm: {
        giornoSett: String(
          props.initialData?.CODICEGGSETT ??
            props.initialData?.PGMEDG_GIORNO_SET ??
            "",
        ),
        giornoSettDescr: String(props.initialData?.DESCRGGSETT ?? ""),
        numMaxPrest: String(
          props.initialData?.MAXESAMI ?? props.initialData?.PGMEDG_NMAX ?? "0",
        ),
        durataStand: String(
          props.initialData?.DURATASTD ??
            props.initialData?.PGMEDG_DURATA_STD ??
            "0",
        ),
        fasce: [
          {
            dalle: formatHour(
              props.initialData?.ORADA1 ?? props.initialData?.PGMEDG_ORE_DA1,
            ),
            alle: formatHour(
              props.initialData?.ORAA1 ?? props.initialData?.PGMEDG_ORE_A1,
            ),
          },
          {
            dalle: formatHour(
              props.initialData?.ORADA2 ?? props.initialData?.PGMEDG_ORE_DA2,
            ),
            alle: formatHour(
              props.initialData?.ORAA2 ?? props.initialData?.PGMEDG_ORE_A2,
            ),
          },
          {
            dalle: formatHour(
              props.initialData?.ORADA3 ?? props.initialData?.PGMEDG_ORE_DA3,
            ),
            alle: formatHour(
              props.initialData?.ORAA3 ?? props.initialData?.PGMEDG_ORE_A3,
            ),
          },
          {
            dalle: formatHour(
              props.initialData?.ORADA4 ?? props.initialData?.PGMEDG_ORE_DA4,
            ),
            alle: formatHour(
              props.initialData?.ORAA4 ?? props.initialData?.PGMEDG_ORE_A4,
            ),
          },
          {
            dalle: formatHour(
              props.initialData?.ORADA5 ?? props.initialData?.PGMEDG_ORE_DA5,
            ),
            alle: formatHour(
              props.initialData?.ORAA5 ?? props.initialData?.PGMEDG_ORE_A5,
            ),
          },
        ],
        modGest: String(
          props.initialData?.FLAGRICORRENZA ??
            props.initialData?.PGMEDG_RICORRENZA ??
            "0",
        ) as "0" | "1" | "2",
        dataDec:
          (props.initialData?.DATADECORRENZA ??
          props.initialData?.PGMEDG_DATA_DECORRENZA)
            ? dayjs(
                props.initialData?.DATADECORRENZA ??
                  props.initialData?.PGMEDG_DATA_DECORRENZA,
              )
            : null,
        agendaGiorniAtt: String(
          props.initialData?.INTERVALLO ??
            props.initialData?.PGMEDG_INTERVALLO ??
            "0",
        ),
        primo:
          String(
            props.initialData?.SWPRIMO ??
              props.initialData?.PGMEDG_SW_PRIMO ??
              "0",
          ) === "1",
        secondo:
          String(
            props.initialData?.SWSECONDO ??
              props.initialData?.PGMEDG_SW_SECONDO ??
              "0",
          ) === "1",
        terzo:
          String(
            props.initialData?.SWTERZO ??
              props.initialData?.PGMEDG_SW_TERZO ??
              "0",
          ) === "1",
        quarto:
          String(
            props.initialData?.SWQUARTO ??
              props.initialData?.PGMEDG_SW_QUARTO ??
              "0",
          ) === "1",
        quinto:
          String(
            props.initialData?.SWULTIMO ??
              props.initialData?.PGMEDG_SW_ULTIMO ??
              "0",
          ) === "1",
      },
    });
  }, [props.open, props.action, props.initialData, modalMethods]);

  const refreshAgenda = async () => {
    const idPgmedt =
      props.parentMethods.getValues("medIntNuovoProgramma.chiave") ||
      (props.createdIdPgmedt as string | undefined);
    if (!idPgmedt)
      throw new Error("Manca la chiave agenda (medIntNuovoProgramma.chiave).");

    const res = await pgmedtage({ crud: "R", codicePgmedt: "", idPgmedt });
    const data = res?.data?.dati?.[0];
    if (!data) return;

    props.parentMethods.setValue(
      "medIntNuovoProgramma.tipologie",
      data.tipoPrenotazione ?? [],
      {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      },
    );
  };

  useEffect(() => {
    if (!showAllert) return;
    if (severity === "warning") return;

    const t1 = setTimeout(() => setShowAllert(false), 4000);
    const t2 = setTimeout(() => {
      setSeverity("warning");
      setMessage("");
    }, 6000);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [showAllert, severity]);

  const askConfirmSubmit = modalMethods.handleSubmit((values) => {
    setPendingSubmit(values);

    setSeverity("warning");
    setMessage(
      props.action === "insert"
        ? "Confermi l'inserimento della prestazione?"
        : props.action === "edit"
          ? "Confermi la modifica della prestazione?"
          : "Confermi la copia della prestazione?",
    );
    setShowAllert(true);
  });

  const submitGiornata = async () => {
    if (!pendingSubmit) return;
    setError(null);
    setLoading(true);

    try {
      const idPgmedt =
        props.parentMethods.getValues("medIntNuovoProgramma.chiave") ||
        (props.createdIdPgmedt as string | undefined);
      if (!idPgmedt) throw new Error("Manca chiave agenda.");

      if (props.action === "insert" && !props.selectedTipologiaId) {
        throw new Error(
          "Seleziona prima una tipologia (serve per inserire una giornata).",
        );
      }

      if (
        props.action !== "insert" &&
        !props.initialData?.IDPGMEDG &&
        !props.initialData?.PGMEDG_PRGINT
      ) {
        throw new Error(
          "Manca ID giornata (IDPGMEDG/PGMEDG_PRGINT) per la modifica.",
        );
      }

      const formValues = pendingSubmit.giornateForm;
      const fasce = formValues.fasce ?? [];

      const isInsert = props.action === "insert";
      const isEdit = props.action === "edit";
      const isCopy = props.action === "copy";

      const crud = isInsert ? "c" : isEdit ? "u" : "p";

      // Prendo l'id della giornata perché serve per update e duplicazione
      const idGiornata = String(
        props.initialData?.IDPGMEDG ?? props.initialData?.PGMEDG_PRGINT ?? "",
      );

      const pgmedObj: any = {
        PGMEDD_PRGINT: String(
          isInsert
            ? props.selectedTipologiaId
            : (props.initialData?.PGMEDD_PRGINT ??
                props.selectedTipologiaId ??
                ""),
        ),

        PGMEDG_GIORNO_SET: String(formValues.giornoSett ?? ""),
        PGMEDG_NMAX: String(formValues.numMaxPrest ?? "0"),
        PGMEDG_DURATA_STD: String(formValues.durataStand ?? "0"),

        PGMEDG_ORE_DA1: toHHmmOr0(fasce[0]?.dalle),
        PGMEDG_ORE_A1: toHHmmOr0(fasce[0]?.alle),
        PGMEDG_ORE_DA2: toHHmmOr0(fasce[1]?.dalle),
        PGMEDG_ORE_A2: toHHmmOr0(fasce[1]?.alle),
        PGMEDG_ORE_DA3: toHHmmOr0(fasce[2]?.dalle),
        PGMEDG_ORE_A3: toHHmmOr0(fasce[2]?.alle),
        PGMEDG_ORE_DA4: toHHmmOr0(fasce[3]?.dalle),
        PGMEDG_ORE_A4: toHHmmOr0(fasce[3]?.alle),
        PGMEDG_ORE_DA5: toHHmmOr0(fasce[4]?.dalle),
        PGMEDG_ORE_A5: toHHmmOr0(fasce[4]?.alle),

        PGMEDG_RICORRENZA: String(formValues.modGest ?? "0"),
        PGMEDG_DATA_DECORRENZA: formValues.dataDec
          ? dayjs(formValues.dataDec).format("YYYY-MM-DD")
          : "",
        PGMEDG_INTERVALLO: String(formValues.agendaGiorniAtt ?? "0"),

        PGMEDG_SW_PRIMO: to01(!!formValues.primo),
        PGMEDG_SW_SECONDO: to01(!!formValues.secondo),
        PGMEDG_SW_TERZO: to01(!!formValues.terzo),
        PGMEDG_SW_QUARTO: to01(!!formValues.quarto),
        PGMEDG_SW_ULTIMO: to01(!!formValues.quinto),
      };

      const payload = isCopy
        ? {
            crud: "p",
            prgint: Number(idGiornata),
            giorno_set: formValues.giornoSett,
          }
        : {
            crud,
            ...(!isInsert ? { prgint: Number(idGiornata) } : {}),
            dati: { pgmedg: pgmedObj },
          };

      const response = await pgmedg(payload);

      if (response?.data?.esito === "OK") {
        setLoading(false);
        setSeverity("success");
        setMessage("Operazione completata con successo.");
      } else {
        setSeverity("error");
        setMessage("Salvataggio non riuscito.");
        return;
      }

      await refreshAgenda();
      setTimeout(() => props.onClose(), 800);
    } catch (e: any) {
      console.error("Errore salvataggio giornata:", e);
      setSeverity("error");
      setMessage(e?.message ?? "Errore durante il salvataggio.");
    } finally {
      setPendingSubmit(null);
      setLoading(false);
    }
  };

  return (
    <>
      <AllertMessage
        dataToSend={pendingSubmit}
        showAllert={showAllert}
        setShowAllert={setShowAllert}
        severity={severity}
        message={message}
        submitFunction={severity === "warning" ? submitGiornata : null}
      />
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 9999,
          }}
        >
          <CircularProgress size="6rem" />
        </Box>
      )}
      <Modal open={props.open}   onClose={(_, reason) => {
          // blocco chiusura su click fuori
          if (reason === "backdropClick") return;

          // blocco su tasto ESC
          if (reason === "escapeKeyDown") return;

          props.onClose();
        }}>
        <Box
          sx={{
            maxHeight: "90vh",
            maxWidth: "190vh",
            width: "100%",
            overflow: "auto",
            paddingTop: 1,
            backgroundColor: "white",
            borderRadius: 2,
            boxShadow: 24,
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <FormProvider {...modalMethods}>
            <Tabs value={0} sx={{ paddingLeft: 2 }}>
              <Tab
                sx={{
                  fontFamily: "Montserrat",
                  fontWeight: "bold",
                  textTransform: "none",
                }}
                label="Giornate - Dettaglio"
              />
            </Tabs>
            <Grid
              container
              spacing={2}
              padding={2}
              display={"flex"}
              sx={{ height: "100%" }}
            >
              <Grid item xs={6} md={6}>
                <RHFAutocomplete
                  name="giornateForm.giornoSett"
                  control={modalMethods.control}
                  options={giornate}
                  label="Giorno Settimana"
                  size="small"
                  getOptionLabel={(o: any) => o?.label ?? ""}
                  isOptionEqualToValue={(o: any, v: any) =>
                    String(o?.value ?? "") ===
                    String(typeof v === "string" ? v : (v?.value ?? ""))
                  }
                  mapIn={(value: any) =>
                    giornate.find(
                      (g) => String(g.value) === String(value ?? ""),
                    ) ?? null
                  }
                  mapOut={(opt: any) => String(opt?.value ?? "")}
                />
              </Grid>
              <Grid item xs={6}></Grid>
              <Grid item xs={12} md={12} display={"flex"}>
                <Grid container display={"flex"} sx={{ height: "100%" }}>
                  <Grid item xs={6} display={"flex"} paddingRight={1}>
                    <Stack direction={"column"} gap={2}>
                      <RHFTextField
                        control={modalMethods.control}
                        name={"giornateForm.numMaxPrest"}
                        label="N° massimo prestazioni"
                        variant="outlined"
                        size="small"
                        fullWidth
                      />
                      <RHFTextField
                        control={modalMethods.control}
                        name={"giornateForm.durataStand"}
                        label="Durata standard"
                        placeholder="Durata standard"
                        variant="outlined"
                        size="small"
                        fullWidth
                      />
                      <Box
                        sx={{
                          display: "flex",
                          borderRadius: "4px",
                          border: "1px solid #CCC",
                          padding: 2,
                          height: "100%",
                        }}
                        alignContent={"stretch"}
                        alignItems={"stretch"}
                      >
                        {" "}
                        <Stack
                          direction={"column"}
                          gap={2}
                          sx={{ height: "100%" }}
                          display={"flex"}
                          alignContent={"stretch"}
                          alignItems={"stretch"}
                        >
                          {fasce.map((_, index) => (
                            <PROVVISORIA
                              key={index}
                              index={index}
                              methods={modalMethods}
                              disabled={!isFasciaEnabled(index)}
                            />
                          ))}
                        </Stack>
                      </Box>
                    </Stack>
                  </Grid>
                  <Grid
                    item
                    xs={6}
                    display={"flex"}
                    sx={{
                      borderRadius: "4px",
                      border: "1px solid #CCC",
                    }}
                  >
                    <Grid container spacing={2} padding={2} display={"flex"}>
                      <Grid item xs={12}>
                        <Typography
                          fontSize={"small"}
                          sx={{ fontWeight: "bold", fontSize: "medium" }}
                        >
                          Ricorrenza
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography
                          display={"flex"}
                          alignContent={"center"}
                          alignItems={"center"}
                          sx={{
                            color: "rgba(0, 0, 0, 0.50)",
                            fontWeight: "bold",
                            fontSize: "14px",
                          }}
                        >
                          Modalità gestione
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <RHFRadioGroup
                          name="giornateForm.modGest"
                          control={modalMethods.control}
                          options={[
                            { label: "No", value: "0" },
                            { label: "Settimanale", value: "1" },
                            { label: "Mensile", value: "2" },
                          ]}
                          row
                          sx={{
                            width: "100%",
                            display: "flex",
                            "& .MuiFormControlLabel-root": {
                              flex: 1,
                              justifyContent: "center",
                            },
                            "& .MuiTypography-root": {
                              color: "rgba(0, 0, 0, 0.70)",
                              fontSize: "14px",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <RHFDateTimePicker
                          name="giornateForm.dataDec"
                          control={modalMethods.control}
                          dateOnly
                          label="Data Decorrenza"
                          size="small"
                          fullWidth
                          disabled={isNo || !isSettimanale}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <RHFTextField
                          control={modalMethods.control}
                          name={"giornateForm.agendaGiorniAtt"}
                          label="Agenda attiva ogni"
                          variant="outlined"
                          size="small"
                          fullWidth
                          disabled={isNo || !isSettimanale}
                        />{" "}
                      </Grid>
                      <Grid item xs={6}>
                        <RHFTextField
                          control={modalMethods.control}
                          name={"giornateForm.giornoSettDescr"}
                          label="Giorno"
                          variant="outlined"
                          size="small"
                          fullWidth
                          disabled
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography
                          sx={{
                            color: "rgba(0, 0, 0, 0.50)",
                            fontWeight: "bold",
                            fontSize: "14px",
                          }}
                        >
                          Agenda attiva il:
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Stack direction={"row"} gap={1}>
                          <RHFCheckbox
                            name="giornateForm.primo"
                            label="Primo"
                            control={modalMethods.control}
                            disabled={isNo || !isMensile}
                          />
                          <RHFCheckbox
                            name="giornateForm.secondo"
                            label="Secondo"
                            control={modalMethods.control}
                            disabled={isNo || !isMensile}
                          />
                          <RHFCheckbox
                            name="giornateForm.terzo"
                            label="Terzo"
                            control={modalMethods.control}
                            disabled={isNo || !isMensile}
                          />
                        </Stack>
                      </Grid>
                      <Grid
                        item
                        xs={6}
                        display={"flex"}
                        alignContent={"center"}
                        alignItems={"center"}
                      >
                        <Stack direction={"row"} gap={0.3}>
                          <RHFCheckbox
                            name="giornateForm.quarto"
                            label="Quarto"
                            control={modalMethods.control}
                            disabled={isNo || !isMensile}
                          />
                          <RHFCheckbox
                            name="giornateForm.quinto"
                            label="Ultimo"
                            control={modalMethods.control}
                            disabled={isNo || !isMensile}
                          />
                        </Stack>
                      </Grid>
                      <Grid
                        item
                        xs={3}
                        display={"flex"}
                        alignContent={"center"}
                        alignItems={"center"}
                      >
                        <RHFTextField
                          control={modalMethods.control}
                          name={"giornateForm.giornoSettDescr"}
                          label="Giorno"
                          variant="outlined"
                          size="small"
                          disabled={isNo || !isMensile}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={3}
                        display={"flex"}
                        alignContent={"center"}
                        alignItems={"center"}
                        justifyContent={"center"}
                      >
                        <Typography
                          sx={{
                            color: "rgba(0, 0, 0, 0.50)",
                            fontWeight: "bold",
                            fontSize: "14px",
                          }}
                        >
                          del mese
                        </Typography>
                      </Grid>
                      <Grid></Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={12} width={"100%"}>
                <Grid
                  container
                  display={"flex"}
                  justifyContent={"space-between"}
                >
                  <Grid item>
                    <Button variant="outlinedCustom" onClick={props.onClose}>
                      Indietro
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      variant="containedCustom"
                      onClick={askConfirmSubmit}
                    >
                      Conferma
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </FormProvider>
        </Box>
      </Modal>
    </>
  );
};

export default GiornateModal;
